/**
 * 상담 전 구간의 루트 문서.
 *
 * 지금까지 "다음에 무슨 화면이 오는가" 는 `CounselStartPage` 의 switch 문과 어댑터들
 * 안에 흩어져 있었다. 규칙 표(`counselStageMachine`)를 뽑아낸 뒤에도 그 표를 읽어서
 * 화면을 고르는 코드는 여전히 앱에 있었다 — 문서만 봐서는 상담이 어디서 시작해 어디로
 * 가는지 알 수 없었다.
 *
 * 이 문서가 그 자리다. 하는 일은 세 가지뿐이다.
 *
 *   1. `data` 로 상담 스냅샷을 읽고 규칙 표를 돌려 지금 스테이지를 구한다.
 *   2. `branch` 로 그 스테이지의 자리로 간다.
 *   3. `chain` 으로 스테이지 문서를 띄운다.
 *
 * 스테이지 문서가 끝나면 이 문서도 끝난다(chain 의 의미). 호스트는 그 완료를 받아
 * 상담을 갱신하고 루트를 다시 세운다 — 그러면 1번부터 다시 돌아 다음 스테이지가 뜬다.
 * 프로덕션이 화면마다 API 를 쏘고 상담을 refetch 해서 다음 단계를 정하는 것과 같은
 * 순환이고, 여기서는 그 순환이 문서에 적혀 있다.
 *
 * 화면이 없는 스텝(transform)만으로 짜여 있다. 라우팅은 사용자가 볼 것이 아니므로
 * 스크린을 두면 스테이지 사이마다 빈 화면이 한 번씩 스친다.
 */
import type { AssetDocument, Expr } from "@miso/design-system";

import { PROGRESS_SUB_STAGES, type ProgressSubStageKind } from "../routes/counselStages";

export const COUNSEL_FULL_FLOW_ID = "counsel-full-flow";

/**
 * 스테이지 → 문서 id.
 *
 * `counselStageMachine.documents` 와 같은 대응이지만 진행하기 7단계가 각자 문서를
 * 갖는다는 사실까지 편다. 규칙 표는 "progress" 하나로 보지만 실제로 뜨는 문서는
 * sub-stage 마다 다르다.
 */
export const PROGRESS_DOCUMENT_IDS: Record<ProgressSubStageKind, string> = {
  "moving-date": "counsel.progress.movingDate",
  "departure-transport": "counsel.progress.departureTransport",
  "arrival-address": "counsel.progress.arrivalAddress",
  "arrival-transport": "counsel.progress.arrivalTransport",
  packing: "counsel.progress.packing",
  "moving-types": "counsel.movingTypes",
  "customer-requests": "counsel.progress.customerRequests",
};

export const STAGE_DOCUMENT_IDS = {
  start: "counsel.start",
  houseStructure: "counsel.houseStructure",
  houseStructureDone: "house-structure.done",
  pickRoom: "counsel.pickRoom",
  movingItems: "counsel.movingItems",
  review: "counsel.review",
  sketchVideo: "counsel.sketchVideo",
  additionalSpaces: "counsel.additionalSpaces",
  complete: "counsel.complete",
} as const;

/** 라우팅 스텝 하나 — 화면 없이 스테이지 문서로 넘긴다. */
function chainStep(target: string): AssetDocument["steps"][string] {
  return {
    kind: "transform",
    code: "return {};",
    outputKey: "_route",
    next: { kind: "chain", target },
  };
}

/** 진행하기 sub-stage 의 라우팅 스텝 id. */
function progressStepId(sub: ProgressSubStageKind): string {
  return `progress:${sub}`;
}

/** `{ '==': [...] }` 는 정확히 두 항짜리 튜플이라 배열 리터럴로는 추론이 안 된다. */
function eq(path: string, value: string): Expr {
  return { "==": [{ var: path }, value] };
}

const BRANCH_CASES: Array<{ if: Expr; then: string }> = [
  { if: eq("stage", "start"), then: "start" },
  { if: eq("stage", "house-structure"), then: "houseStructure" },
  { if: eq("stage", "moving-items"), then: "movingItems" },
  { if: eq("stage", "pick-room"), then: "pickRoom" },
  ...PROGRESS_SUB_STAGES.map((sub) => ({
    if: { and: [eq("stage", "progress"), eq("sub", sub)] } as Expr,
    then: progressStepId(sub),
  })),
  /*
   * 리뷰만 규칙 표에 없다. 진행하기가 끝나면 프로덕션은 곧장 스케치 영상으로 가지
   * 않고 상담 현황을 한 번 보여준다(intake 세션이 아직 COMPLETED 가 아닐 때).
   * 그 관문이 지금까지 페이지 컴포넌트 안에만 있었다 — 여기서는 분기의 한 줄이다.
   */
  {
    /*
     * `not` 은 Expr 만 받는다 — `{ var: … }` 는 경로지 식이 아니다. 값 비교로 쓰면
     * 아직 리뷰를 안 지난 상담(필드가 없는 경우 포함)이 모두 걸린다.
     */
    if: {
      and: [
        { "!=": [{ var: "counsel.reviewed" }, true] },
        { or: [eq("stage", "sketch-video"), eq("stage", "additional-spaces")] },
      ],
    } as Expr,
    then: "review",
  },
  { if: eq("stage", "sketch-video"), then: "sketchVideo" },
  { if: eq("stage", "additional-spaces"), then: "additionalSpaces" },
];

const doc: AssetDocument = {
  id: COUNSEL_FULL_FLOW_ID,
  schemaVersion: 2,

  /**
   * 상담을 읽고 규칙 표를 돌린다.
   *
   * `counsel.stage` derive 가 `resolveStage(counselStageMachine, ...)` 다. 프로덕션과
   * 프리뷰가 같은 표를 쓰고, 술어 구현만 호스트가 채운다. 결과(`stage`/`sub`/`target`)를
   * 스코프에 펴서 아래 분기가 곧바로 읽는다.
   */
  data: [
    { id: "counsel", source: "counsel.snapshot" },
    {
      id: "_stage",
      derive: "counsel.stage",
      args: { counsel: { $var: "counsel" } },
      spread: true,
    },
  ],

  initial: "route",
  steps: {
    /** 규칙이 고른 스테이지의 자리로 보낸다. */
    route: {
      kind: "transform",
      code: "return {};",
      outputKey: "_route",
      next: { kind: "branch", when: BRANCH_CASES, else: "complete" },
    },

    start: chainStep(STAGE_DOCUMENT_IDS.start),
    houseStructure: chainStep(STAGE_DOCUMENT_IDS.houseStructure),
    pickRoom: chainStep(STAGE_DOCUMENT_IDS.pickRoom),
    movingItems: chainStep(STAGE_DOCUMENT_IDS.movingItems),
    review: chainStep(STAGE_DOCUMENT_IDS.review),
    sketchVideo: chainStep(STAGE_DOCUMENT_IDS.sketchVideo),
    additionalSpaces: chainStep(STAGE_DOCUMENT_IDS.additionalSpaces),
    complete: chainStep(STAGE_DOCUMENT_IDS.complete),

    ...Object.fromEntries(
      PROGRESS_SUB_STAGES.map((sub) => [
        progressStepId(sub),
        chainStep(PROGRESS_DOCUMENT_IDS[sub]),
      ]),
    ),
  },

  meta: {
    title: "상담 전체 플로우",
    description:
      "상담 스냅샷 → 스테이지 규칙 → 스테이지 문서. 문서가 끝나면 호스트가 상담을 갱신하고 이 문서를 다시 세운다.",
  },
};

export const COUNSEL_FULL_FLOW = { doc };
